import { get } from 'lodash';
import invariant from './invariant';

const ASC = 'asc';
const DESC = 'desc';

function validateSortBy(sortBy) {
  invariant(
    ['string', 'function'].includes(typeof sortBy),
    'Expected sortBy of type string or function',
  );
}

function compareNumbers(a, b, descending) {
  const aIsNumber = typeof a === 'number';
  const bIsNumber = typeof b === 'number';

  // pushing falsy values to the bottom of the list
  if (!aIsNumber && bIsNumber) return 1;
  if (aIsNumber && !bIsNumber) return -1;

  return descending ? b - a : a - b;
}

function compareStrings(a, b, descending) {
  const aIsString = typeof a === 'string';
  const bIsString = typeof b === 'string';

  // pushing falsy values to the bottom of the list
  if (!aIsString && bIsString) return 1;
  if (aIsString && !bIsString) return -1;

  return a.localeCompare(b) * (descending ? -1 : 1);
}

/**
 * Create a compare function to compare two objects based on the value at path
 * @param {String|Function} path A path to a property to sort by or a function
 * that returns the value to compare.
 * @param {String} [order="asc"] The sort order. Defaults to an ascending order.
 *   specify an order of "desc" for descending or "asc" for ascending sort order
 * @return {Function} A function that compares two objects at the given path.
 *   Can be used as the callback function of Array.prototype.sort
 */
export function createCompareFn(sortBy, order = ASC) {
  validateSortBy(sortBy);
  return function compareFn(objectA, objectB) {
    const sortByIsFunction = typeof sortBy === 'function';
    const a = sortByIsFunction ? sortBy(objectA) : get(objectA, sortBy);
    const b = sortByIsFunction ? sortBy(objectB) : get(objectB, sortBy);

    const aType = typeof a;
    const bType = typeof b;

    if (aType === 'number' || bType === 'number') {
      return compareNumbers(a, b, order === DESC);
    }
    if (aType === 'string' || bType === 'string') {
      return compareStrings(a, b, order === DESC);
    }
    // We are only handling comparision when the values of A or B are either
    // numbers or strings. For any other types, we are going to skip the
    // comparision and treat A or B as if they are equal. This means A and B
    // will remain unchanged with respect to each other.
    return 0;
  };
}

/**
 * Sort an array of objects
 * @param {Array} array An array of objects to sort
 * @param {String|Function} path A path to a property to sort by or a function
 * that returns the value to sort by.
 * @param {String} [order="asc"] The sort order. Defaults to an ascending order.
 *   specify an order of "desc" for descending or "asc" for ascending sort order
 * @return {Array} A sorted array
 */
export function sort(array, sortBy, order) {
  invariant(Array.isArray(array), 'Expected an array to sort');
  validateSortBy(sortBy);
  return [...array].sort(createCompareFn(sortBy, order));
}
